import React, { useState, useEffect, useRef } from "react";
import {
    Card,
    Space,
    Form,
    Input,
    Button,
    message,
    Upload,
    Image,
    Select,
    Divider,
    Radio,
    Tooltip,
    InputNumber,
    Row,
    Col,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Cookies from "universal-cookie";
import {
    ArrowLeftOutlined,
    UploadOutlined,
    PlusOutlined,
    MinusCircleOutlined,
    QuestionCircleOutlined,
    SaveOutlined,
} from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Exam from "./Exam";
import LessonManager from "./LessonManager";

const CourseForm = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const cookies = new Cookies();
    const auth = cookies.get("authToken");
    const config = { headers: { "auth-token": auth, "Content-Type": "multipart/form-data" } };
    const [data, setData] = useState();
    const [description, setDescription] = useState("");
    const quillRef = useRef(null);
    const [thresholdType, setThresholdType] = useState("percentage");

    const uploadProps = {
        beforeUpload: () => false, // ป้องกันการอัพโหลดอัตโนมัติ
        maxCount: 1,
    };

    useEffect(() => {
        if (id) {
            fetchCourse();
        }
    }, [id]);

    useEffect(() => {
        // Set up image handler for Quill
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            const toolbar = quill.getModule("toolbar");
            toolbar.addHandler("image", handleImageUpload);
        }
    }, [quillRef]);

    const fetchCourse = async () => {
        try {
            const response = await axios.get(`/api/courses/${id}`, config);
            setData(response.data);
            setDescription(response.data.description || "");
            setThresholdType(response.data.exam_pass_threshold_type || "percentage");
            form.setFieldsValue({
                ...response.data,
                links: response.data.links || [],
            });
        } catch (error) {
            message.error("ไม่สามารถดึงข้อมูลใบประกาศได้");
            navigate("/course");
        }
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("title", values.title);
            formData.append("excerpt", values.excerpt || "");
            formData.append("description", description);
            formData.append("status", values.status);
            formData.append("category", values.category || "");
            formData.append("exam_pass_threshold", values.exam_pass_threshold || "");
            formData.append("exam_pass_threshold_type", values.exam_pass_threshold_type || "");
            formData.append("exam_time_limit", values.exam_time_limit || "");
            formData.append("exam_question_count", values.exam_question_count || "");
            formData.append("exam_choice_count", values.exam_choice_count || "");
            if (values.links && values.links.length > 0) {
                formData.append("links", JSON.stringify(values.links));
            }
            console.log(JSON.stringify(values.links));
            if (values.image?.[0]) {
                formData.append("image", values.image[0].originFileObj);
            }
            if (values.video?.[0]) {
                formData.append("video", values.video[0].originFileObj);
            }

            if (id) {
                await axios.put(`/api/courses/${id}`, formData, config);
                message.success("แก้ไขข้อมูลใบประกาศเรียบร้อยแล้ว");
            } else {
                await axios.post(`/api/courses`, formData, config);
                message.success("สร้างข้อมูลใบประกาศเรียบร้อยแล้ว");
            }
            navigate(-1);
        } catch (error) {
            message.error("Error: " + error.response.data.message);
        }
        setLoading(false);
    };

    const validateUrl = (_, value) => {
        try {
            new URL(value);
            return Promise.resolve();
        } catch {
            return Promise.reject("Please enter a valid URL");
        }
    };

    // Quill editor modules and formats configuration
    const modules = {
        toolbar: {
            container: [
                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                ["bold", "italic", "underline", "strike", "blockquote"],
                [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
                ["link", "image", "video"],
                // ["clean"],
            ],
        },
    };

    const formats = [
        "header",
        "bold",
        "italic",
        "underline",
        "strike",
        "blockquote",
        "list",
        "bullet",
        "indent",
        "link",
        "image",
        "video",
    ];

    const handleImageUpload = () => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            if (file) {
                // Show loading message
                message.loading("Uploading image...", 0);

                // Create form data
                const formData = new FormData();
                formData.append("image", file);

                try {
                    // Upload the image
                    const response = await axios.post("/api/courses/upload-image", formData, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    });

                    // Get the URL from the response
                    const imageUrl = response.data.file.url;

                    // Get the Quill editor instance
                    const quill = quillRef.current.getEditor();

                    // Get current cursor position
                    const range = quill.getSelection(true);

                    // Insert the image at the cursor position
                    quill.insertEmbed(range.index, "image", imageUrl);

                    // Move cursor after the image
                    quill.setSelection(range.index + 1);

                    // Close loading message and show success
                    message.destroy();
                    message.success("Image uploaded successfully");
                } catch (error) {
                    message.destroy();
                    message.error("Failed to upload image");
                }
            }
        };
    };

    return (
        <>
            <Row gutter={[16, 16]} align="center" justify="space-between" style={{ marginBottom: "20px" }}>
                <Col>
                    <Space align="baseline" justify="space-between">
                        <ArrowLeftOutlined onClick={() => navigate(-1)} />
                        <h2>{id ? "แก้ไขใบประกาศ" : "สร้างใบประกาศ"}</h2>
                    </Space>
                </Col>
                {/* <Col>
                    <Button style={{ width: "200px" }} type="primary" htmlType="submit" loading={loading} block>
                        {id ? (
                            <>
                                <SaveOutlined />
                                {` บันทึกการแก้ไขบทเรียน`}
                            </>
                        ) : (
                            <>
                                <PlusOutlined />
                                {` สร้างบทเรียน`}
                            </>
                        )}
                    </Button>
                </Col> */}
            </Row>

            <Card>
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Button
                        style={{ width: "200px", position: "absolute", right: 0, top: "-50px" }}
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        block
                    >
                        <SaveOutlined />
                        {id ? <>{` บันทึกการแก้ไขใบประกาศ`}</> : <>{` สร้างใบประกาศ`}</>}
                    </Button>

                    <Form.Item
                        name="title"
                        label="ชื่อใบประกาศ"
                        rules={[{ required: true, message: "กรุณากรอกชื่อใบประกาศ" }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item name="excerpt" label="รายละเอียดแบบย่อ (ตัวอักษร)">
                        <Input />
                    </Form.Item>

                    <label htmlFor="" style={{ marginBottom: "8px", display: "block" }}>
                        รายละเอียด
                    </label>
                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={description}
                        onChange={setDescription}
                        modules={modules}
                        formats={formats}
                        style={{ minHeight: "200px", marginBottom: "20px" }}
                    />

                    <Form.Item name="category" label="หมวดหมู่">
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="รูปภาพปก"
                        name="image"
                        getValueFromEvent={(e) => {
                            if (Array.isArray(e)) {
                                return e;
                            }
                            return e?.fileList; // คืนค่าเป็น fileList
                        }}
                    >
                        <Upload {...uploadProps} accept="image/*" listType="picture">
                            {data && data.image_path && (
                                <Image width="200px" src={`/${data.image_path}`} className="mb-2" />
                            )}
                            <Button icon={<UploadOutlined />}>เลือกรูปภาพ</Button>
                        </Upload>
                    </Form.Item>

                    <Form.List name="links">
                        {(fields, { add, remove }, { errors }) => (
                            <>
                                <Form.Item style={{ marginBottom: "0" }}>
                                    <Space align="baseline" style={{ marginBottom: 8 }}>
                                        <label>ลิงค์ภายนอก</label>
                                        <Button type="link" onClick={() => add()} icon={<PlusOutlined />}>
                                            เพิ่มลิงค์
                                        </Button>
                                    </Space>
                                </Form.Item>

                                {fields.map((field, index) => (
                                    <Space
                                        key={field.key}
                                        style={{ display: "flex", marginBottom: 8 }}
                                        align="baseline"
                                    >
                                        <Form.Item
                                            {...field}
                                            name={[field.name, "title"]}
                                            rules={[{ required: true, message: "Missing link title" }]}
                                            style={{ marginBottom: "5px" }}
                                        >
                                            <Input placeholder="Link Title" style={{ width: 200 }} />
                                        </Form.Item>

                                        <Form.Item
                                            {...field}
                                            name={[field.name, "url"]}
                                            rules={[
                                                { required: true, message: "Missing URL" },
                                                { validator: validateUrl },
                                            ]}
                                            style={{ marginBottom: "5px" }}
                                        >
                                            <Input placeholder="URL" style={{ width: 400 }} />
                                        </Form.Item>

                                        <MinusCircleOutlined onClick={() => remove(field.name)} />
                                    </Space>
                                ))}

                                <Form.ErrorList errors={errors} />
                            </>
                        )}
                    </Form.List>

                    <Form.Item
                        name="status"
                        label="สถานะ"
                        rules={[{ required: true, message: "กรุณาเลือกสถานะ" }]}
                        style={{ marginTop: "20px" }}
                    >
                        <Select>
                            <Select.Option value="publish">Publish</Select.Option>
                            <Select.Option value="draft">Draft</Select.Option>
                        </Select>
                    </Form.Item>

                    <Divider orientation="left"></Divider>

                    <Card title="การตั้งค่าการสอบ" style={{ marginBottom: 24 }}>
                        <Col xs={24} md={12}>
                            <Form.Item label="เวลาในการสอบ" required>
                                <Space align="baseline">
                                    <Form.Item
                                        name="exam_time_limit"
                                        noStyle
                                        rules={[{ required: true, message: "กรุณาระบุเวลาในการสอบ" }]}
                                    >
                                        <InputNumber min={1} max={180} style={{ width: 100 }} />
                                    </Form.Item>
                                    <span>นาที</span>
                                    <Tooltip title="กำหนดเวลาสูงสุดที่อนุญาตให้ทำข้อสอบได้">
                                        <QuestionCircleOutlined />
                                    </Tooltip>
                                </Space>
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item label="เกณฑ์ผ่าน" required>
                                <Space align="baseline" style={{ flexWrap: "wrap" }}>
                                    <Form.Item name="exam_pass_threshold_type" noStyle>
                                        <Radio.Group
                                            onChange={(e) => setThresholdType(e.target.value)}
                                            value={thresholdType}
                                        >
                                            <Radio value="percentage">เปอร์เซ็นต์ %</Radio>
                                            <Radio value="count">จำนวนข้อ</Radio>
                                        </Radio.Group>
                                    </Form.Item>

                                    <Form.Item
                                        name="exam_pass_threshold"
                                        noStyle
                                        rules={[{ required: true, message: "กรุณาระบุเกณฑ์ผ่าน" }]}
                                    >
                                        <InputNumber
                                            min={1}
                                            max={thresholdType === "percentage" ? 100 : 999}
                                            style={{ width: 100, margin: "0 8px" }}
                                        />
                                    </Form.Item>

                                    <span>{thresholdType === "percentage" ? "%" : "ข้อ"}</span>

                                    <Tooltip
                                        title={`กำหนดจำนวน ${
                                            thresholdType === "percentage" ? "เปอร์เซ็นต์ %" : "จำนวนข้อ"
                                        } ในการสอบผ่าน`}
                                    >
                                        <QuestionCircleOutlined />
                                    </Tooltip>
                                </Space>
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item label="จำนวนข้อสอบ">
                                <Space align="baseline">
                                    <Form.Item name="exam_question_count" noStyle>
                                        <InputNumber min={1} max={180} style={{ width: 100 }} />
                                        {/* <InputNumber min={1} max={180} style={{ width: 100 }} /> */}
                                    </Form.Item>
                                    <span>ข้อ</span>
                                    <Tooltip title="เว้นว่างเพื่อแสดงข้อสอบทั้งหมด หรือระบุจำนวนที่ต้องการให้แสดง">
                                        <QuestionCircleOutlined />
                                    </Tooltip>
                                </Space>
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item label="จำนวนตัวเลือกในแต่ละข้อ">
                                <Space align="baseline">
                                    <Form.Item name="exam_choice_count" noStyle>
                                        <InputNumber min={1} max={180} style={{ width: 100 }} />
                                    </Form.Item>
                                    <span>ตัวเลือก</span>
                                    <Tooltip title="เว้นว่างเพื่อแสดงตัวเลือกทั้งหมด หรือระบุจำนวนตัวเลือกที่ต้องการให้แสดงในแต่ละข้อ">
                                        <QuestionCircleOutlined />
                                    </Tooltip>
                                </Space>
                            </Form.Item>
                        </Col>

                        {/* <Col xs={24} md={12}>
                            <Form.Item
                                name="exam_question_count"
                                label="จำนวนข้อสอบ"
                                tooltip="จำนวนข้อสอบที่จะแสดงให้ผู้เรียนทำ (ถ้าไม่ระบุจะแสดงทั้งหมด)"
                            >
                                <Space align="baseline">
                                    <InputNumber min={1} max={999} style={{ width: 100 }} placeholder="ไม่จำกัด" />
                                    <span>ข้อ</span>
                                    <Tooltip title="เว้นว่างเพื่อแสดงข้อสอบทั้งหมด หรือระบุจำนวนที่ต้องการให้แสดง">
                                        <QuestionCircleOutlined />
                                    </Tooltip>
                                </Space>
                            </Form.Item>
                        </Col> */}

                        {/* <Col xs={24} md={12}>
                            <Form.Item
                                name="exam_choice_count"
                                label="จำนวนตัวเลือกในแต่ละข้อ"
                                tooltip="จำนวนตัวเลือกที่จะแสดงในแต่ละข้อ (ถ้าไม่ระบุจะแสดงทั้งหมด)"
                            >
                                <Space align="baseline">
                                    <InputNumber min={2} max={6} style={{ width: 100 }} placeholder="ไม่จำกัด" />
                                    <span>ตัวเลือก</span>
                                    <Tooltip title="เว้นว่างเพื่อแสดงตัวเลือกทั้งหมด หรือระบุจำนวนตัวเลือกที่ต้องการให้แสดงในแต่ละข้อ">
                                        <QuestionCircleOutlined />
                                    </Tooltip>
                                </Space>
                            </Form.Item>
                        </Col> */}
                    </Card>

                    <Form.Item>
                        <Button style={{ marginTop: "10px" }} type="primary" htmlType="submit" loading={loading} block>
                            <SaveOutlined />
                            {id ? "บันทึกการแก้ไขใบประกาศ" : "สร้างใบประกาศ"}
                        </Button>
                    </Form.Item>
                </Form>

                {id && <LessonManager courseId={id} />}
                {id && <Exam courseId={id} />}
            </Card>
        </>
    );
};

export default CourseForm;
